import React, { useMemo, useRef } from 'react';
import { Box, Button, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Typography } from '@cbreone/core-ui';
import useExpandableText from '../../hooks/useExpandableText';

type Props = {
  text: string | number | null | undefined;
  collapsedLines?: number;
  fallbackText?: string;
  expandLabel?: string;
  collapseLabel?: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4px',
    },
    text: {
      whiteSpace: 'pre-line',
      width: '100%',
    },
    clampedText: {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    toggleButton: {
      minWidth: 'auto',
      padding: 0,
      textTransform: 'none',
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      lineHeight: '18px',
      textDecoration: 'underline',
      '& .MuiButton-label': {
        fontSize: '12px !important',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      },
    },
  }),
);

const ExpandableTextComponent: React.FC<Props> = ({
  text,
  collapsedLines = 3,
  fallbackText = '-',
  expandLabel = 'See More',
  collapseLabel = 'See Less',
}) => {
  const classes = useStyles();
  const contentIdRef = useRef(`expandable-text-${Math.random().toString(36).slice(2, 10)}`);

  const normalizedText = useMemo(() => {
    if (text === undefined || text === null) {
      return '';
    }

    return String(text).trim();
  }, [text]);

  const hasValue = normalizedText.length > 0;

  const {
    contentRef,
    expanded,
    showToggle,
    toggleExpanded,
  } = useExpandableText({
    text: normalizedText,
    collapsedLines,
  });

  const label = expanded ? collapseLabel : expandLabel;

  return (
    <Box className={classes.root}>
      <Typography
        id={contentIdRef.current}
        ref={contentRef}
        variant="body1"
        className={`${classes.text} ${!expanded && hasValue ? classes.clampedText : ''}`}
        style={!expanded && hasValue ? { WebkitLineClamp: collapsedLines } : undefined}
        tabIndex={hasValue ? 0 : -1}
      >
        {hasValue ? normalizedText : fallbackText}
      </Typography>

      {showToggle && hasValue && (
        <Button
          type="button"
          variant="text"
          className={classes.toggleButton}
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={contentIdRef.current}
        >
          {label}
        </Button>
      )}
    </Box>
  );
};

const propsAreEqual = (previousProps: Props, nextProps: Props) => (
  previousProps.text === nextProps.text
  && previousProps.collapsedLines === nextProps.collapsedLines
  && previousProps.fallbackText === nextProps.fallbackText
  && previousProps.expandLabel === nextProps.expandLabel
  && previousProps.collapseLabel === nextProps.collapseLabel
);

export const ExpandableText = React.memo(ExpandableTextComponent, propsAreEqual);

export default ExpandableText;
